import React, { useRef, useEffect } from "react";
import Navigation from "../components/Navigation";
import HomeOverlay from "../components/HomeOverlay";
import Footer from "../components/Footer";
import { useScrolled } from "../hooks";
import "../styles/about.css";

const SCROLL_THRESHOLD = 500;

/* ============ SVG ICONS ============ */
const Icons = {
  engine: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M8 10h2M14 10h2M8 14h8" />
    </svg>
  ),
  design: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l2.4 5.5L20 9.6l-4 3.9.9 5.5L12 16.4 7.1 19l.9-5.5-4-3.9 5.6-1.1z" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6l-5 6 5 6M16 6l5 6-5 6M13 4l-2 16" />
    </svg>
  ),
  tools: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a4 4 0 00-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 005.4-5.4l-2.5 2.5-2.5-2.5 2-3z" />
    </svg>
  ),
};

/* ============ DATA ============ */

const intro = {
  imgSrc: "/assets/img/about1.png",
  imgAlt: "Portrait of Thanh Tung Tran, game developer based in Hanoi",
  fullName: "Thanh Tung Tran",
  role: "Game Developer",
  tagline: "I build games that feel good to play.",
  bio: "Unity developer focused on gameplay systems, level flow and game feel.",
  stats: [
    { value: "4+", label: "Years coding" },
    { value: "10+", label: "Projects" },
    { value: "2", label: "Awards" },
  ],
  facts: [
    { label: "Engine", value: "Unity · C#" },
    { label: "Base", value: "Hanoi, VN" },
  ],
  strengths: [
    "Player-centric design",
    "Gameplay systems development",
    "Proactive problem-solving",
    "Self-learning & adaptability",
  ],
};

const toolkit = [
  { icon: "engine", title: "Engine", items: ["Unity"] },
  { icon: "design", title: "Game Design", items: ["Gameplay Systems", "Level Design", "Game Feel"] },
  { icon: "code", title: "Programming", items: ["C#", "2D/3D Gameplay", "Physics", "UI Programming"] },
  { icon: "tools", title: "Tools", items: ["Unity Editor", "Visual Studio", "Git"] },
];

const hobbies = [
  {
    number: "01",
    title: "FOOTBALL",
    line: "Teamwork, strategy, never give up.",
    imgSrc: "/assets/img/hb-football.png",
    imgAlt: "Football",
  },
  {
    number: "02",
    title: "PLAYING GAME",
    line: "Exploring worlds, one game at a time.",
    imgSrc: "/assets/img/hb-playgame.png",
    imgAlt: "Play game",
  },
  {
    number: "03",
    title: "RESEARCH",
    line: "Solving problems with science.",
    imgSrc: "/assets/img/hb-research.png",
    imgAlt: "Research",
  },
];

const education = {
  logo: "/assets/img/logo-hunre.png",
  logoAlt: "HUNRE - Hanoi University of Natural Resources and Environment logo",
  school: "Hanoi University of Natural Resources and Environment",
  short: "HUNRE",
  major: "Information Technology",
  years: "2022 – 2026",
};

const awards = [
  {
    imgSrc: "/assets/img/awards-ex.png",
    imgAlt: "Excellence in Scientific Research award",
    level: "Faculty Level",
    title: "Excellence in Scientific Research",
    year: "2025",
    medal: "gold",
  },
  {
    imgSrc: "/assets/img/awards-3.png",
    imgAlt: "Third Prize in Scientific Research",
    level: "University Level",
    title: "Third Prize in Scientific Research",
    year: "2025",
    medal: "bronze",
  },
];

/* ============ MAIN PAGE ============ */

const AboutPage = () => {
  const scrolled = useScrolled(SCROLL_THRESHOLD);

  const introRef = useRef(null);
  const toolRef = useRef(null);
  const hobbyRef = useRef(null);
  const eduRef = useRef(null);

  useEffect(() => {
    const sections = [introRef, toolRef, hobbyRef, eduRef];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -80px 0px" }
    );
    sections.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <main className={`about-main${scrolled ? " scrolled" : ""}`} role="main">
      <HomeOverlay />
      <Navigation />

      {/* ========== 01. INTRODUCTION ========== */}
      <section
        id="introduction"
        className="intro-section"
        ref={introRef}
        aria-labelledby="intro-heading"
      >
        <div className="container-1400">
          <div className="section-head">
            <span className="section-eyebrow">01 — Introduction</span>
            <h1 id="intro-heading" className="section-title">
              About me
            </h1>
          </div>

          <div className="intro-layout">
            {/* Cột 1: ảnh */}
            <div className="intro-portrait">
              <div className="intro-portrait-frame">
                <img
                  src={intro.imgSrc}
                  alt={intro.imgAlt}
                  loading="lazy"
                  width="280"
                  height="350"
                />
              </div>
            </div>

            {/* Cột 2: tên + role + tagline + bio */}
            <div className="intro-main">
              <h2 className="intro-name">{intro.fullName}</h2>
              <p className="intro-role">{intro.role}</p>
              <p className="intro-tagline">{intro.tagline}</p>
              <p className="intro-bio">{intro.bio}</p>
            </div>

            {/* Cột 3: stats + facts + strengths */}
            <aside className="intro-side" aria-label="At a glance">
              <div className="intro-stats">
                {intro.stats.map((stat, i) => (
                  <div className="intro-stat" key={i}>
                    <span className="stat-value">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>

              <ul className="intro-facts">
                {intro.facts.map((fact, i) => (
                  <li key={i}>
                    <span className="fact-label">{fact.label}</span>
                    <span className="fact-value">{fact.value}</span>
                  </li>
                ))}
              </ul>

              <div className="intro-strengths">
                <h3 className="strengths-title">Strengths</h3>
                <ul className="strengths-list">
                  {intro.strengths.map((s, i) => (
                    <li key={i}>
                      <span className="strength-mark" aria-hidden="true" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ========== 02. TOOLKIT ========== */}
      <section
        id="toolkit"
        className="toolkit-section"
        ref={toolRef}
        aria-labelledby="toolkit-heading"
      >
        <div className="container-1400">
          <div className="section-head">
            <span className="section-eyebrow">02 — Toolkit</span>
            <h2 id="toolkit-heading" className="section-title">
              What I work with
            </h2>
          </div>

          <div className="toolkit-rows">
            {toolkit.map((group, i) => (
              <div className="toolkit-row" key={i}>
                <div className="toolkit-row-left">
                  <span className="toolkit-icon">{Icons[group.icon]}</span>
                  <h3 className="toolkit-title">{group.title}</h3>
                </div>
                <ul className="toolkit-tags">
                  {group.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 03. HOBBIES ========== */}
      <section
        id="hobbies"
        className="hobbies-section"
        ref={hobbyRef}
        aria-labelledby="hobbies-heading"
      >
        <div className="container-1400">
          <div className="section-head">
            <span className="section-eyebrow">03 — Hobbies</span>
            <h2 id="hobbies-heading" className="section-title">
              Outside the screen
            </h2>
          </div>

          <div className="hobbies-grid">
            {hobbies.map((hobby, i) => (
              <article className="hobby-card" key={i}>
                <div className="hobby-card-media">
                  <img
                    src={hobby.imgSrc}
                    alt={hobby.imgAlt}
                    loading="lazy"
                    width="400"
                    height="267"
                  />
                  <span className="hobby-card-number" aria-hidden="true">
                    {hobby.number}
                  </span>
                </div>
                <div className="hobby-card-text">
                  <h3>{hobby.title}</h3>
                  <p>{hobby.line}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 04. EDUCATION + AWARDS ========== */}
      <section
        id="education"
        className="edu-section"
        ref={eduRef}
        aria-labelledby="edu-heading"
      >
        <div className="container-1400">
          <div className="section-head">
            <span className="section-eyebrow">04 — Education</span>
            <h2 id="edu-heading" className="section-title">
              Study &amp; Achievements
            </h2>
          </div>

          <div className="edu-grid">
            <article
              className="edu-card"
              itemScope
              itemType="https://schema.org/EducationalOccupationalCredential"
            >
              <div className="edu-logo-wrap">
                <img
                  src={education.logo}
                  alt={education.logoAlt}
                  loading="lazy"
                  width="64"
                  height="64"
                />
              </div>
              <div className="edu-info">
                <span className="edu-short">{education.short}</span>
                <h3
                  className="edu-school"
                  itemProp="recognizedBy"
                  itemScope
                  itemType="https://schema.org/CollegeOrUniversity"
                >
                  <span itemProp="name">{education.school}</span>
                </h3>
                <p className="edu-major">{education.major}</p>
                <p className="edu-years">
                  <span className="edu-years-label">Years</span>
                  <span className="edu-years-value">{education.years}</span>
                </p>
              </div>
            </article>

            <div className="awards-col">
              <h3 className="awards-col-title">Achievements</h3>
              <div className="awards-col-list">
                {awards.map((award, i) => (
                  <article
                    className="award-item"
                    key={i}
                    itemScope
                    itemType="https://schema.org/Award"
                  >
                    <div
                      className={`award-medal medal-${award.medal}`}
                      aria-hidden="true"
                    >
                      <img src={award.imgSrc} alt={award.imgAlt} loading="lazy" />
                    </div>
                    <div className="award-info">
                      <span className="award-level">{award.level}</span>
                      <h4 className="award-title" itemProp="name">
                        {award.title}
                      </h4>
                      <span className="award-year">
                        <time dateTime={award.year}>{award.year}</time>
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default AboutPage;